import CatalogContext from '@nile/banyan/src/Context/CatalogContext';
import {
  getParsedCrudData,
  preFormatForm,
  SaveCard,
  serviceItemConstraints,
  validateForm
} from '@nile/components';
import { Page, TextStyle } from '@superops/ui-design-system';
import { omit, pick } from 'lodash';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import CatalogForm from './CatalogForm';

/**
 * Parent component which uses catalog form.
 * All the state manipulation happens here through context.
 * @public
 */
class CatalogSheet extends Component {
  constructor(props) {
    super(props);
    this.state = {
      serviceItem: props.serviceItem
        ? {
            ...props.serviceItem,
            selectedBlockHourAdjustedItems:
              props.serviceItem.blockHoursAdjustedItems || [],
            selectedBlockItemAdjustedItems:
              props.serviceItem.blockItemAdjustedItems || [],
            blockItemUsedIn:
              props.serviceItem.blockItemUsedIn || 'BUSINESS_HOURS'
          }
        : {
            name: null,
            invoiceDescription: null,
            quantityType: 'UNITS',
            // subscriptionType: null,
            frequency: {
              type: null,
              unit: null,
              quantity: null,
              recurringMode: null,
              recurringBefore: null
            },
            hoursType: null,
            roundUpValue: null,
            unitPrice: null,
            category: {
              name: null
            },
            businessHoursUnitPrice: null,
            afterHoursUnitPrice: null,
            useAsWorklogItem: true,
            adjustBlockItemAgainstAllItems: false,
            quantity: null,
            amount: null,
            blockItemUsedIn: 'BUSINESS_HOURS',
            blockHoursAdjustedItems: [],
            blockItemAdjustedItems: [],
            selectedBlockItemAdjustedItems: [],
            selectedBlockHourAdjustedItems: [],
            addBlockHoursAdjustedItems: [],
            deleteBlockHoursAdjustedItems: [],
            carryForwardRemainingBlockHours: false,
            autoRenew: false,
            salesTaxEnabled: false,
            salesTax: null
          },
      submitActionLoading: false
    };
  }

  /**
   * Handling validation before saving.
   * The catalog form is used in multiple places. So it gives all the data.
   * Use inputFields props to filter the required props to be passed to backend.
   * @public
   */
  handleSave = () => {
    const {
      selectedBlockItemAdjustedItems,
      blockItemAdjustedItems
    } = this.state.serviceItem;
    this.setState({ submitActionLoading: true });
    let processedItem = pick(this.state.serviceItem, this.props.inputFields);
    if (!['BLOCK_HOURS', 'BLOCK_MONEY'].includes(processedItem.quantityType)) {
      processedItem.blockItemUsedIn = null;
    }
    if (processedItem?.quantityType !== 'UNITS') {
      processedItem.useAsWorklogItem = undefined;
    }
    if (processedItem?.quantityType === 'BLOCK_HOURS') {
      processedItem.adjustBlockItemAgainstAllItems = false;
    }
    if (!processedItem.category?.name) {
      processedItem = omit(processedItem, ['category']);
    }
    if (!processedItem.salesTaxEnabled) {
      processedItem.salesTax = null;
    }
    const parsedBlockHourObj = getParsedCrudData(
      blockItemAdjustedItems || [],
      selectedBlockItemAdjustedItems || [],
      'itemId'
    );
    if (parsedBlockHourObj.addedItems) {
      processedItem.addBlockItemAdjustedItems = parsedBlockHourObj.addedItems.map(
        (el) => {
          return {
            itemId: el.itemId,
            name: el.name
          };
        }
      );
    }
    if (parsedBlockHourObj.deletedItems) {
      processedItem.deleteBlockItemAdjustedItems = parsedBlockHourObj.deletedItems.map(
        (el) => {
          return {
            itemId: el.itemId,
            name: el.name
          };
        }
      );
    }
    const validationResult = validateForm(
      preFormatForm(processedItem),
      serviceItemConstraints
    );
    if (validationResult) {
      this.setState({ errors: validationResult, submitActionLoading: false });
      return;
    }
    let params = {
      item: processedItem
    };
    if (this.props.serviceItem) {
      params = {
        ...params,
        itemId: processedItem.itemId
      };
    }
    this.props.actions
      .primaryAction({
        variables: params
      })
      .then(() => {
        this.setState({ submitActionLoading: false });
      })
      .catch((err) => {
        this.setState({ submitActionLoading: false });
      });
  };

  /**
   * Method passed through context to update properties
   */
  updateProperties = (properties) => {
    this.setState({
      serviceItem: {
        ...this.state.serviceItem,
        ...properties
      }
    });
  };

  render() {
    const { intl, actions, taxRates, fullWidth = true } = this.props;
    const { errors, submitActionLoading, serviceItem } = this.state;
    const providerValue = {
      value: serviceItem,
      updateProperties: this.updateProperties
    };
    const configurableSections = ['worklogHours', 'costPrice'];
    return (
      <div className="full-height">
        <CatalogContext.Provider value={providerValue}>
          <div
            className={`full-height ${!this.props.removePadding &&
              'p20'} white-grey-page ${!fullWidth && 'w-1200'}`}
          >
            <Page
              variant="primary"
              contentHeight="110px"
              title={
                <TextStyle weight="Bold">
                  {this.props.serviceItem ? (
                    <FormattedMessage id="admin.serviceCatalog.edit" />
                  ) : (
                    <FormattedMessage id="admin.serviceCatalog.new" />
                  )}
                </TextStyle>
              }
              primaryAction={
                <SaveCard
                  handleSave={this.handleSave}
                  actions={actions}
                  loading={submitActionLoading}
                  isNewLayout
                />
              }
            >
              <CatalogForm
                serviceItem={serviceItem}
                actions={actions}
                errors={errors}
                configurableSections={configurableSections}
                disabledSections={
                  serviceItem?.integrationState?.pax8
                    ? ['changeServiceItemType', 'unitPrice', 'useAsWorklog']
                    : []
                }
                pax8={serviceItem?.integrationState?.pax8}
                formType="service-catalog"
                editAndDelete
                onlyProduct={this.props.onlyProduct}
              />
            </Page>
          </div>
        </CatalogContext.Provider>
      </div>
    );
  }
}

export default injectIntl(CatalogSheet);
